/**
 * Utilitário para obter o caminho da logo baseado no Tenant ID.
 * 
 * Lógica de detecção:
 * 1. Desenvolvimento: usa VITE_TENANT_ID do .env.local
 * 2. Produção: detecta pelo subdomínio usando mapeamento fixo
 * 3. Fallback: 'dev' (usa logo_dev.svg)
 * 
 * IMPORTANTE: Em produção, o mesmo build roda em múltiplos subdomínios,
 * então a detecção é sempre feita pelo subdomínio, não pelo .env.
 * 
 * @see src/utils/getTenantId.ts - Lógica similar para tenant ID
 */

import logoOne1Print from '../images/logo_one1print.svg';
import logoOne1PrintDf from '../images/logo_one1printdf.svg';
import logoOneUniformes from '../images/logo_oneuniformes.svg';
import logoDev from '../images/logo_dev.svg';

// Mapeamento de tenant IDs para logos
const LOGO_MAP: Record<string, string> = {
  'one1print': logoOne1Print,
  'one1printdf': logoOne1PrintDf,
  'oneuniformes': logoOneUniformes,
  'dev': logoOne1Print,
};

/**
 * Mapeamento de subdomínios de produção para tenant IDs.
 * 
 * ⚠️ ATENÇÃO: Este mapeamento é usado quando o mesmo build roda em múltiplos subdomínios.
 * Se adicionar novos subdomínios, atualize este mapeamento.
 * 
 * Subdomínios de produção:
 * - corelgo.one1tec.com.br → one1print
 * - corel.one1tec.com.br → one1printdf
 * - corel-uniforme.one1tec.com.br → oneuniformes
 */
const SUBDOMAIN_TO_TENANT_MAP: Record<string, string> = {
  'corelgo': 'one1print',
  'corel': 'one1printdf',
  'corel-uniforme': 'oneuniformes',
};

/**
 * Retorna o tenant ID atual baseado no ambiente.
 * 
 * Em desenvolvimento: usa VITE_TENANT_ID do .env.local
 * Em produção: detecta pelo subdomínio usando SUBDOMAIN_TO_TENANT_MAP
 * 
 * @returns O tenant ID (one1print, one1printdf, oneuniformes, ou dev)
 */
function getCurrentTenantId(): string {
  // Em desenvolvimento, usar variável de ambiente se definida
  if (import.meta.env.DEV) {
    const envTenantId = import.meta.env.VITE_TENANT_ID;
    if (envTenantId && typeof envTenantId === 'string' && envTenantId.trim() !== '') {
      return envTenantId.trim();
    }
    // Fallback para 'dev' em desenvolvimento
    return 'dev';
  }

  // Em produção, detectar pelo subdomínio
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];

    // Verificar se o subdomínio está no mapeamento
    if (SUBDOMAIN_TO_TENANT_MAP[subdomain]) {
      return SUBDOMAIN_TO_TENANT_MAP[subdomain];
    }
  }

  // Fallback final
  return 'dev';
}

/**
 * Retorna o caminho da logo baseado no tenant ID atual.
 * 
 * A logo é selecionada dinamicamente baseada no ambiente:
 * - Desenvolvimento: usa VITE_TENANT_ID do .env.local
 * - Produção: detecta pelo subdomínio automaticamente
 * 
 * @returns O caminho da logo SVG correspondente ao tenant
 * 
 * @example
 * ```tsx
 * import { getTenantLogo } from '../utils/getTenantLogo';
 * 
 * <img src={getTenantLogo()} alt="Logo" />
 * ```
 */
export function getTenantLogo(): string {
  const tenantId = getCurrentTenantId();
  return LOGO_MAP[tenantId] || LOGO_MAP['dev'];
}

